import type { OktaAccessQuery } from "src/graphql/generated";

export type OktaAccess = OktaAccessQuery["oktaAccess"][number];

type Tone = "slate" | "green" | "amber" | "red";

/**
 * What the Okta access cell shows for one person. No tone means plain text rather
 * than a badge: the person has no Okta user, so there is no Okta status to show.
 */
export type OktaAccessSummary = { label: string; tone?: Tone; caption?: string };

/**
 * Okta's statuses as Okta's own Admin Console labels them, so the column reads the
 * same as the page an admin checks it against. PROVISIONED is the one whose name
 * misleads: it is an activated user who has not yet set a password.
 */
const statuses: Record<string, { label: string; tone: Tone }> = {
  STAGED: { label: "Staged", tone: "slate" },
  PROVISIONED: { label: "Pending user action", tone: "amber" },
  ACTIVE: { label: "Active", tone: "green" },
  RECOVERY: { label: "Password reset", tone: "amber" },
  PASSWORD_EXPIRED: { label: "Password expired", tone: "amber" },
  LOCKED_OUT: { label: "Locked out", tone: "red" },
  SUSPENDED: { label: "Suspended", tone: "red" },
  DEPROVISIONED: { label: "Deactivated", tone: "slate" }
};

export function describeOktaAccess(access: OktaAccess): OktaAccessSummary {
  if (!access.login) {
    return { label: "No work email" };
  }
  if (!access.status) {
    return { label: "Not in Okta" };
  }
  // A status Okta adds later still renders, in its own words.
  const known = statuses[access.status] ?? {
    label: sentenceCase(access.status),
    tone: "slate" as const
  };
  if (access.lastLogin) {
    return { ...known, caption: `Last sign-in ${new Date(access.lastLogin).toLocaleDateString()}` };
  }
  // Only said of an active user, where it is the news: someone staged or pending
  // has never signed in by definition.
  if (access.status === "ACTIVE") {
    return { ...known, caption: "Never signed in" };
  }
  return known;
}

function sentenceCase(status: string): string {
  const words = status.toLowerCase().replaceAll("_", " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}
